import { Box, IconButton, Typography, Chip, Link as MuiLink } from "@mui/material";
import { Close, OpenInNew } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { AssistantItem as AssistantItemType } from "../../store/store.types";
import { useStore } from "../../store/store";
import { CallButton } from "../phoneLetsCall/CallButton";

type AssistantItemProps = {
    item: AssistantItemType;
};

const TYPE_CONFIG = {
    task: {
        label: "Tarefa",
        color: "#e3f2fd",
        borderColor: "#1976d2",
    },
    reserved_slot: {
        label: "Reserva",
        color: "#fff3e0",
        borderColor: "#f57c00",
    },
    kiosk_callback: {
        label: "Tarefa",
        color: "#fdecea",
        borderColor: "#d32f2f",
    },
};

export const AssistantItem = ({ item }: AssistantItemProps) => {
    const { assistantDialog } = useStore();
    const config = TYPE_CONFIG[item.type];

    const treatmentUrl = item.treatmentId ? `/treatments/${item.treatmentId}/detail` : "#";
    const clientUrl = item.clientLinkPath ?? treatmentUrl;

    const handleDismiss = () => {
        assistantDialog.removeItem(item.id);
    };

    return (
        <Box
            sx={{
                backgroundColor: "white",
                borderRadius: 3,
                p: 2,
                borderLeft: `4px solid ${config.borderColor}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <MuiLink
                            component={Link}
                            to={clientUrl}
                            variant="subtitle2"
                            fontWeight={600}
                            color="text.primary"
                            noWrap={item.type !== "kiosk_callback"}
                            underline="hover"
                            sx={{ cursor: "pointer" }}
                        >
                            {item.clientName}
                        </MuiLink>
                        <Chip
                            component={Link}
                            to={item.linkPath}
                            label={config.label}
                            size="small"
                            clickable
                            sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                backgroundColor: config.color,
                                color: config.borderColor,
                                fontWeight: 500,
                                textDecoration: "none",
                            }}
                        />
                    </Box>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.4,
                        }}
                    >
                        {item.contentLinkPath ? (
                            <MuiLink component={Link} to={item.contentLinkPath} color="inherit" underline="hover">
                                {item.content}
                            </MuiLink>
                        ) : (
                            item.content
                        )}
                    </Typography>
                    {item.phone && (
                        <Box sx={{ mt: 1 }}>
                            <CallButton phone={item.phone} />
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <IconButton
                        component={Link}
                        to={item.linkPath}
                        target="_blank"
                        size="small"
                        sx={{
                            color: "primary.main",
                            "&:hover": { backgroundColor: "primary.light", color: "white" },
                        }}
                        title="Abrir em nova aba"
                    >
                        <OpenInNew fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={handleDismiss}
                        sx={{
                            color: "text.secondary",
                            "&:hover": { backgroundColor: "error.light", color: "white" },
                        }}
                        title="Dispensar"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

